package fyi.moko.hotpl.data.repository

import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.SyncAction
import fyi.moko.hotpl.domain.model.Visit
import kotlinx.browser.localStorage
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

@Serializable
internal data class JsSyncQueueEntry(
    val queueId: Long,
    val entityType: String,
    val entityId: String,
    val action: String,
    val createdAt: Long,
)

@Serializable
internal data class JsPlacesSnapshot(
    val places: List<Place> = emptyList(),
    val visits: List<Visit> = emptyList(),
    val syncQueue: List<JsSyncQueueEntry> = emptyList(),
)

/**
 * Durable browser storage for the web place repository — the JS analog of Room. The
 * whole places/visits snapshot is written to `localStorage` as JSON so data survives a
 * page refresh. (Photo bytes are handled separately by `JsPlaceImageStore`.)
 *
 * Reads/writes are defensive: a missing or corrupt entry just starts the app empty
 * rather than throwing.
 *
 * The storage key is parameterized by [userId] so that different accounts on the same
 * browser profile are isolated: `hotpl.repository.v1.<userId>` (or `…guest` when no
 * user is signed in). This prevents the stale-data-bleed where account A's local
 * places would be adopted into account B after a sign-in switch (backlog 0-8).
 */
internal object JsPlaceStore {
    private const val KEY_PREFIX = "hotpl.repository.v1."
    private const val GUEST_SUFFIX = "guest"

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    private fun keyFor(userId: String?): String =
        KEY_PREFIX + (userId?.takeIf { it.isNotBlank() } ?: GUEST_SUFFIX)

    fun load(userId: String?): JsPlacesSnapshot? {
        val raw = try {
            localStorage.getItem(keyFor(userId))
        } catch (e: Throwable) {
            null
        } ?: return null
        return try {
            json.decodeFromString(JsPlacesSnapshot.serializer(), raw)
        } catch (e: Throwable) {
            console.error("Failed to read persisted places; starting empty", e)
            null
        }
    }

    fun save(userId: String?, snapshot: JsPlacesSnapshot) {
        try {
            localStorage.setItem(keyFor(userId), json.encodeToString(JsPlacesSnapshot.serializer(), snapshot))
        } catch (e: Throwable) {
            console.error("Failed to persist places to localStorage", e)
        }
    }
}
